import type { FC, ReactNode } from 'react';
import { Divider } from 'antd';
import dayjs from 'dayjs';
import type { OptimizeResultConfig, ResultSummaryFieldConfig } from '../../../../config/optimizeResultConfig';
import type { OptimiseResultSummaryData } from '../../../../types/optimiseResult';

interface Props {
    summary: OptimiseResultSummaryData;
    config: OptimizeResultConfig;
}

function renderFieldContent(
    field: ResultSummaryFieldConfig,
    baseInputs: Record<string, unknown>,
): ReactNode {
    const value = baseInputs[field.name];
    if (value == null) return null;

    switch (field.type) {
        case 'date': {
            const formatted = dayjs.unix(Number(value)).format(field.dateFormat ?? 'MM/DD/YYYY');
            return (
                <span className="text-sm">
                    <span className="text-[#18181b]">{field.label}:</span>{' '}
                    <span className="text-[#595959]">{formatted}</span>
                </span>
            );
        }
        case 'keepTopWithContext': {
            const template = field.contextTemplate ?? 'Keep {value} events out of {totalCount}';
            const keepValue = String(value);
            const totalCount = String(baseInputs[field.totalCountField ?? 'total_promo_count'] ?? '-');
            const segments = template.split(/(\{value\}|\{totalCount\})/);
            return (
                <span className="text-sm text-[#18181b]">
                    {segments.map((seg, idx) => {
                        if (seg === '{value}')
                            return <span key={idx} className="text-[#8c8c8c]">{keepValue}</span>;
                        if (seg === '{totalCount}')
                            return <span key={idx} className="text-[#8c8c8c]">{totalCount}</span>;
                        return <span key={idx}>{seg}</span>;
                    })}
                </span>
            );
        }
        case 'text':
        default:
            return (
                <span className="text-sm">
                    <span className="text-[#18181b]">{field.label}:</span>{' '}
                    <span className="text-[#595959]">{String(value)}</span>
                </span>
            );
    }
}

const OptimiseResultSummary: FC<Props> = ({ summary, config }) => {
    const { selected_attributes, base_inputs } = summary;
    const baseInputsRecord = base_inputs as unknown as Record<string, unknown>;

    return (
        <div className="flex flex-col gap-4 border border-[#f0f0f0] rounded-md">
            <div className="px-4 pt-4">
                <p className="text-xs text-[#8c8c8c] mb-2 font-medium">{config.scopeSectionLabel}</p>
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                    {selected_attributes.flatMap((attr) =>
                        attr.attributeValues.map((val) => (
                            <span key={`${attr.attributeType}-${val}`} className="text-sm text-[#18181b]">
                                {val}
                            </span>
                        )),
                    )}
                </div>
            </div>

            <Divider className="!my-0" />

            <div className="px-4 pb-4">
                <p className="text-xs text-[#8c8c8c] mb-2 font-medium">{config.baseInputSectionLabel}</p>
                <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                    {config.baseInputDisplayFields.map((field) => {
                        const content = renderFieldContent(field, baseInputsRecord);
                        if (content == null) return null;
                        return <span key={field.name}>{content}</span>;
                    })}
                </div>
            </div>
        </div>
    );
};

OptimiseResultSummary.displayName = 'OptimiseResultSummary';
export default OptimiseResultSummary;
